// Invisible Cloudflare Turnstile integration. When REACT_APP_TURNSTILE_SITEKEY
// is unset the helpers no-op, so the form layer behaves exactly as before.
//
// When the sitekey IS set:
//   * The Turnstile script loads lazily on the first getTurnstileToken() call.
//   * We render a 0×0 invisible widget, request a token, and return it.
//   * The token is sent as `cf-turnstile-response` header — backend MUST verify.
//
// See BACKEND-FORM-HARDENING.md for the backend-side requirements.

const SITEKEY = process.env.REACT_APP_TURNSTILE_SITEKEY || '';
const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

let scriptPromise = null;

const loadScript = () => {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${SCRIPT_URL}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(window.turnstile || null));
      existing.addEventListener('error', () => reject(new Error('Turnstile script failed to load')));
      return;
    }
    const script = document.createElement('script');
    script.src = SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.turnstile || null);
    script.onerror = () => reject(new Error('Turnstile script failed to load'));
    document.head.appendChild(script);
  });

  return scriptPromise;
};

const ensureContainer = () => {
  let container = document.getElementById('tn-turnstile-host');
  if (container) return container;
  container = document.createElement('div');
  container.id = 'tn-turnstile-host';
  // Off-screen, zero-size — never visible, never affects layout.
  container.style.cssText = 'position:absolute;left:-9999px;top:-9999px;width:0;height:0;overflow:hidden;';
  document.body.appendChild(container);
  return container;
};

export const isTurnstileEnabled = () => Boolean(SITEKEY);

export const getTurnstileToken = async () => {
  if (!SITEKEY) return null;
  try {
    const turnstile = await loadScript();
    if (!turnstile?.render) return null;

    return await new Promise((resolve) => {
      const host = ensureContainer();
      let settled = false;
      const finish = (token) => {
        if (settled) return;
        settled = true;
        resolve(token || null);
      };
      // 10s ceiling so a stalled Turnstile never blocks the form forever —
      // backend MUST treat a missing token as a verification failure if it
      // requires the header, but should accept a submit without the header
      // when the sitekey isn't configured.
      const timer = setTimeout(() => finish(null), 10000);
      try {
        turnstile.render(host, {
          sitekey: SITEKEY,
          size: 'invisible',
          callback: (token) => {
            clearTimeout(timer);
            finish(token);
          },
          'error-callback': () => {
            clearTimeout(timer);
            finish(null);
          },
          'timeout-callback': () => {
            clearTimeout(timer);
            finish(null);
          },
        });
      } catch (_err) {
        clearTimeout(timer);
        finish(null);
      }
    });
  } catch (_err) {
    return null;
  }
};
